import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCandidates } from "../slice/candidateSlice";
import { fetchClients } from "../slice/clientSlice";
import { createOptimization, previewParseJd, clearOptimizationError } from "../slice/optimizationSlice";
import api from "../api/axiosInstance";
import "../css/NewOptimization.css";

// Small inline icons so the page has no extra icon-library dependency.
const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zM19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9L19 15z"
      fill="currentColor"
    />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Eligibility tiles are rendered from this list instead of three
// copy-pasted label blocks.
const ELIGIBILITY_OPTIONS = [
  { key: "usCitizenship", title: "US citizenship", desc: "Candidate must be a US citizen" },
  { key: "greenCard", title: "Green card / permanent resident", desc: "Citizens or permanent residents only" },
  { key: "securityClearance", title: "Security clearance", desc: "Active or obtainable clearance required" },
];

export default function NewOptimization() {
  const [candidateId, setCandidateId] = useState("");
  const [clientId, setClientId] = useState("");

  const [jdTitle, setJdTitle] = useState("");
  const [jdUrl, setJdUrl] = useState("");
  const [jdText, setJdText] = useState("");

  // Job titles already used in past optimizations — shown as suggestions
  // under the Job Title input. The input stays free-text, so a brand-new
  // title can still be typed (or filled in by the AI parse).
  const [jobTitleOptions, setJobTitleOptions] = useState([]);

  const [requiredExperience, setRequiredExperience] = useState("");
  const [requiredSkills, setRequiredSkills] = useState("");
  const [preferredSkills, setPreferredSkills] = useState("");

  // Step 4 eligibility inputs.
  const [usCitizenshipRequired, setUsCitizenshipRequired] = useState(false);
  const [greenCardRequired, setGreenCardRequired] = useState(false);
  const [securityClearanceRequired, setSecurityClearanceRequired] = useState(false);
  const [otherRequirement, setOtherRequirement] = useState("");

  const [parsingJdPreview, setParsingJdPreview] = useState(false);
  // Remembers the exact text the preview parsed, plus the Preferred
  // Background it found (that field has no input box on this form). On
  // submit, if the pasted text is still identical, the backend is told it
  // doesn't need to parse the JD again.
  const [previewParse, setPreviewParse] = useState(null); // { text, preferredBackground }

  // Display-only: which fields the AI parse filled in. Used to highlight
  // them for review; a field loses its highlight once the recruiter edits it.
  const [aiFilled, setAiFilled] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { candidates } = useSelector((state) => state.candidates);
  const { clients } = useSelector((state) => state.clients);
  const { status, error } = useSelector((state) => state.optimizations);
  const loading = status === "loading";

  useEffect(() => {
    dispatch(fetchCandidates());
    dispatch(fetchClients());

    // Load job title suggestions. Best-effort: if this fails, the Job
    // Title input still works as a normal text box.
    api
      .get("/optimization/job-titles")
      .then((res) => setJobTitleOptions(res.data.jobTitles || []))
      .catch((err) => console.error("Failed to load job title suggestions", err));

    return () => {
      dispatch(clearOptimizationError());
    };
  }, [dispatch]);

  // Only candidates that already have a master resume on file are worth
  // selecting here — the backend requires one to start an optimization.
  // Resumes are uploaded from the candidate's own page, not from here.
  const candidatesWithResume = candidates.filter((c) => c.latestMasterResume);

  const selectedCandidate = candidatesWithResume.find((c) => c._id === candidateId);
  const selectedClient = clients.find((c) => c._id === clientId);
  const previewStillValid = !!previewParse && previewParse.text === jdText;

  // Wraps a setter so a manual edit clears that field's "AI filled" mark.
  const manualEdit = (setter, key) => (value) => {
    setter(value);
    if (aiFilled[key]) setAiFilled((prev) => ({ ...prev, [key]: false }));
  };

  const eligibilityState = {
    usCitizenship: [usCitizenshipRequired, manualEdit(setUsCitizenshipRequired, "usCitizenship")],
    greenCard: [greenCardRequired, manualEdit(setGreenCardRequired, "greenCard")],
    securityClearance: [securityClearanceRequired, manualEdit(setSecurityClearanceRequired, "securityClearance")],
  };

  // Parses the pasted JD text and pre-fills fields — only fills fields
  // that are currently blank, and only ever turns a checkbox ON (never
  // off), so it never clobbers anything the recruiter already entered.
  const handleParseJdPreview = async () => {
    if (!jdText.trim()) {
      alert("Paste the job description text above first, then parse it.");
      return;
    }
    setParsingJdPreview(true);
    try {
      const result = await dispatch(previewParseJd(jdText));
      if (previewParseJd.rejected.match(result)) {
        alert(`AI JD parsing failed: ${result.payload || "Unknown error."}`);
        return;
      }
      const parsed = result.payload;
      const elig = parsed.eligibilityConstraints || {};
      const filled = {};

      setPreviewParse({ text: jdText, preferredBackground: parsed.preferredBackground || [] });

      if (!jdTitle && parsed.title) {
        setJdTitle(parsed.title);
        filled.jdTitle = true;
      }
      if (requiredExperience === "" && parsed.requiredExperience != null) {
        setRequiredExperience(String(parsed.requiredExperience));
        filled.requiredExperience = true;
      }
      if (!requiredSkills.trim() && parsed.requiredSkills?.length) {
        setRequiredSkills(parsed.requiredSkills.join(", "));
        filled.requiredSkills = true;
      }
      if (!preferredSkills.trim() && parsed.preferredSkills?.length) {
        setPreferredSkills(parsed.preferredSkills.join(", "));
        filled.preferredSkills = true;
      }
      if (!otherRequirement && elig.otherRequirement) {
        setOtherRequirement(elig.otherRequirement);
        filled.otherRequirement = true;
      }
      if (elig.usCitizenshipRequired && !usCitizenshipRequired) {
        setUsCitizenshipRequired(true);
        filled.usCitizenship = true;
      }
      if (elig.greenCardRequired && !greenCardRequired) {
        setGreenCardRequired(true);
        filled.greenCard = true;
      }
      if (elig.securityClearanceRequired && !securityClearanceRequired) {
        setSecurityClearanceRequired(true);
        filled.securityClearance = true;
      }

      setAiFilled((prev) => ({ ...prev, ...filled }));
    } finally {
      setParsingJdPreview(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearOptimizationError());

    if (!candidateId) {
      alert("Please select a candidate.");
      return;
    }
    if (!clientId) {
      alert("Please select a client.");
      return;
    }
    if (!jdText.trim()) {
      alert("Please paste the job description text.");
      return;
    }

    // Still sent as FormData so the backend route keeps receiving the same format.
    const optForm = new FormData();
    optForm.append("candidateId", candidateId);
    optForm.append("clientId", clientId);
    optForm.append("jdTitle", jdTitle);
    optForm.append("jdUrl", jdUrl);
    optForm.append("jdText", jdText);
    if (requiredExperience !== "") optForm.append("requiredExperience", requiredExperience);
    optForm.append("requiredSkills", requiredSkills);
    optForm.append("preferredSkills", preferredSkills);
    optForm.append("usCitizenshipRequired", usCitizenshipRequired);
    optForm.append("greenCardRequired", greenCardRequired);
    optForm.append("securityClearanceRequired", securityClearanceRequired);
    optForm.append("otherRequirement", otherRequirement);

    // Skip a second AI parse on the backend only when the preview parsed exactly this text.
    if (previewStillValid) {
      optForm.append("jdAlreadyParsed", "true");
      // Sent as JSON because each entry is a sentence that may contain commas.
      optForm.append("preferredBackgroundJson", JSON.stringify(previewParse.preferredBackground));
    }

    const result = await dispatch(createOptimization(optForm));

    if (createOptimization.fulfilled.match(result)) {
      navigate(`/dashboard/optimizations/${result.payload._id}`);
    }
  };

  const fieldClass = (key) => `nopt-field${aiFilled[key] ? " is-ai" : ""}`;
  const AiTag = ({ k }) => (aiFilled[k] ? <span className="nopt-ai-tag">AI filled</span> : null);

  const checklist = [
    { done: !!candidateId, label: "Candidate selected" },
    { done: !!clientId, label: "Client selected" },
    { done: !!jdText.trim(), label: "Job description pasted" },
  ];

  const masterResume = selectedCandidate?.latestMasterResume || null;

  return (
    <div className="nopt-page">
      <header className="nopt-header">
        <h1>Start optimization</h1>
        <p>Tailor a candidate's master resume to a specific job description.</p>
      </header>

      {error && (
        <div className="nopt-error" role="alert">
          {error}
        </div>
      )}

      <form className="nopt-layout" onSubmit={handleSubmit}>
        <div className="nopt-main">
          {/* Step 1 */}
          <section className="nopt-card">
            <div className="nopt-card-head">
              <span className="nopt-step">1</span>
              <div>
                <h2>Candidate &amp; client</h2>
                <p>Who is applying, and who the role is for.</p>
              </div>
            </div>

            <div className="nopt-grid nopt-grid-2">
              <div className="nopt-field">
                <label htmlFor="nopt-candidate">
                  Candidate <span className="nopt-req">*</span>
                </label>
                <select id="nopt-candidate" value={candidateId} onChange={(e) => setCandidateId(e.target.value)} required>
                  <option value="">Select a candidate…</option>
                  {candidatesWithResume.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} ({c.email})
                    </option>
                  ))}
                </select>
                {candidatesWithResume.length === 0 && (
                  <p className="nopt-hint">No candidates with a master resume yet. Add one from the Candidates page first.</p>
                )}
                {masterResume && (
                  <p className="nopt-hint">
                    Using master resume: <strong>{masterResume.fileName}</strong>
                    {masterResume.version ? ` (version ${masterResume.version})` : ""}. To use a newer resume, upload it
                    from the candidate's page first.
                  </p>
                )}
              </div>

              <div className="nopt-field">
                <label htmlFor="nopt-client">
                  Client <span className="nopt-req">*</span>
                </label>
                <select id="nopt-client" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                  <option value="">Select a client…</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {clients.length === 0 && <p className="nopt-hint">No clients yet. Add one from the Clients page first.</p>}
              </div>
            </div>
          </section>

          {/* Step 2 */}
          <section className="nopt-card">
            <div className="nopt-card-head">
              <span className="nopt-step">2</span>
              <div>
                <h2>Job description</h2>
                <p>Paste the full posting. AI can fill in the fields below from it.</p>
              </div>
            </div>

            <div className="nopt-grid nopt-grid-2">
              <div className={fieldClass("jdTitle")}>
                <label htmlFor="nopt-title">
                  Job title <AiTag k="jdTitle" />
                </label>
                <input
                  id="nopt-title"
                  type="text"
                  list="job-title-options"
                  value={jdTitle}
                  onChange={(e) => manualEdit(setJdTitle, "jdTitle")(e.target.value)}
                  placeholder="e.g. Senior Java Developer"
                  autoComplete="off"
                />
                <datalist id="job-title-options">
                  {jobTitleOptions.map((title) => (
                    <option key={title} value={title} />
                  ))}
                </datalist>
              </div>

              <div className="nopt-field">
                <label htmlFor="nopt-url">Job posting URL</label>
                <input
                  id="nopt-url"
                  type="text"
                  value={jdUrl}
                  onChange={(e) => setJdUrl(e.target.value)}
                  placeholder="https://…"
                />
              </div>
            </div>

            <div className="nopt-field">
              <label htmlFor="nopt-jd">
                Job description text <span className="nopt-req">*</span>
              </label>
              <div className="nopt-jd">
                <textarea
                  id="nopt-jd"
                  rows={10}
                  value={jdText}
                  onChange={(e) => setJdText(e.target.value)}
                  placeholder="Paste the job description here…"
                  required
                />
                <div className="nopt-jd-bar">
                  <span className="nopt-jd-status">
                    {previewStillValid
                      ? "Parsed. Review the highlighted fields below."
                      : previewParse
                      ? "Text changed since the last parse."
                      : "Optional. If you skip this, the JD is parsed when you start."}
                  </span>
                  <button
                    type="button"
                    className="nopt-btn nopt-btn-ai"
                    onClick={handleParseJdPreview}
                    disabled={parsingJdPreview || !jdText.trim()}
                  >
                    <SparkleIcon />
                    {parsingJdPreview ? "Parsing…" : previewStillValid ? "Parse again" : "Parse with AI"}
                  </button>
                </div>
              </div>
              <p className="nopt-hint">AI only fills fields you've left blank, so nothing you've typed gets overwritten.</p>
            </div>
          </section>

          {/* Step 3 */}
          <section className="nopt-card">
            <div className="nopt-card-head">
              <span className="nopt-step">3</span>
              <div>
                <h2>Requirements</h2>
                <p>Separate skills with commas or put one per line.</p>
              </div>
            </div>

            <div className={`${fieldClass("requiredExperience")} nopt-narrow`}>
              <label htmlFor="nopt-exp">
                Required experience <AiTag k="requiredExperience" />
              </label>
              <div className="nopt-suffix">
                <input
                  id="nopt-exp"
                  type="number"
                  min="0"
                  step="0.5"
                  value={requiredExperience}
                  onChange={(e) => manualEdit(setRequiredExperience, "requiredExperience")(e.target.value)}
                  placeholder="0"
                />
                <span>years</span>
              </div>
            </div>

            <div className="nopt-grid nopt-grid-2">
              <div className={fieldClass("requiredSkills")}>
                <label htmlFor="nopt-req-skills">
                  Required skills <AiTag k="requiredSkills" />
                </label>
                <textarea
                  id="nopt-req-skills"
                  rows={4}
                  value={requiredSkills}
                  onChange={(e) => manualEdit(setRequiredSkills, "requiredSkills")(e.target.value)}
                  placeholder="Java, Spring Boot, AWS"
                />
              </div>

              <div className={fieldClass("preferredSkills")}>
                <label htmlFor="nopt-pref-skills">
                  Preferred skills <AiTag k="preferredSkills" />
                </label>
                <textarea
                  id="nopt-pref-skills"
                  rows={4}
                  value={preferredSkills}
                  onChange={(e) => manualEdit(setPreferredSkills, "preferredSkills")(e.target.value)}
                  placeholder="Kubernetes, GraphQL"
                />
              </div>
            </div>
          </section>

          {/* Step 4 */}
          <section className="nopt-card">
            <div className="nopt-card-head">
              <span className="nopt-step">4</span>
              <div>
                <h2>Eligibility</h2>
                <p>Work authorization rules set by the client.</p>
              </div>
            </div>

            <div className="nopt-tiles">
              {ELIGIBILITY_OPTIONS.map((opt) => {
                const [checked, setChecked] = eligibilityState[opt.key];
                return (
                  <label
                    key={opt.key}
                    className={`nopt-tile${checked ? " is-on" : ""}${aiFilled[opt.key] ? " is-ai" : ""}`}
                  >
                    <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                    <span className="nopt-tile-box">
                      <CheckIcon />
                    </span>
                    <span className="nopt-tile-text">
                      <span className="nopt-tile-title">
                        {opt.title} <AiTag k={opt.key} />
                      </span>
                      <span className="nopt-tile-desc">{opt.desc}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className={fieldClass("otherRequirement")}>
              <label htmlFor="nopt-other">
                Other client requirement <AiTag k="otherRequirement" />
              </label>
              <input
                id="nopt-other"
                type="text"
                value={otherRequirement}
                onChange={(e) => manualEdit(setOtherRequirement, "otherRequirement")(e.target.value)}
                placeholder="e.g. Must not require future visa sponsorship"
              />
            </div>
          </section>
        </div>

        {/* Sticky summary + action */}
        <aside className="nopt-side">
          <div className="nopt-summary">
            <h2>Ready to optimize</h2>

            <ul className="nopt-checklist">
              {checklist.map((item) => (
                <li key={item.label} className={item.done ? "is-done" : ""}>
                  <span className="nopt-dot">{item.done && <CheckIcon />}</span>
                  {item.label}
                </li>
              ))}
              <li className={previewStillValid ? "is-done" : "is-soft"}>
                <span className="nopt-dot">{previewStillValid && <CheckIcon />}</span>
                {previewStillValid ? "JD parsed with AI" : "AI parse runs on start"}
              </li>
            </ul>

            {(selectedCandidate || selectedClient || jdTitle) && (
              <dl className="nopt-picked">
                {selectedCandidate && (
                  <>
                    <dt>Candidate</dt>
                    <dd>{selectedCandidate.name}</dd>
                  </>
                )}
                {selectedClient && (
                  <>
                    <dt>Client</dt>
                    <dd>{selectedClient.name}</dd>
                  </>
                )}
                {jdTitle && (
                  <>
                    <dt>Role</dt>
                    <dd>{jdTitle}</dd>
                  </>
                )}
              </dl>
            )}

            <button type="submit" className="nopt-btn nopt-btn-primary" disabled={loading}>
              {loading ? "Creating & parsing JD…" : "Start optimization"}
            </button>
            <p className="nopt-side-note">You'll go to the optimization page once it's created.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
